import { useEffect, useState } from 'react';
import { useQuery, skipToken } from '@tanstack/react-query';

// Below this a term matches half the country, so the search is not worth a round trip. It mirrors
// the floor the server applies to the address read ([AS-037]) — the one read both callers of this
// hook use. A second read with a different floor is the moment to make this a parameter, and not
// before: a configurable floor with one value is a knob that documents nothing.
const MIN_TERM_LENGTH = 3;
const DEBOUNCE_MS = 300;

const isSearchable = (term: string): boolean => term.length >= MIN_TERM_LENGTH;

export interface UseDebouncedSearch<T> {
  data: T | undefined;
  /** The debounced term `data` answers; `undefined` until a search settles. */
  resolvedQuery: string | undefined;
  isTooShort: boolean;
  /** A searchable term with no answer yet — the debounce window and the request in flight alike. */
  isSearching: boolean;
  isError: boolean;
  refetch: () => void;
}

export interface DebouncedSearchOptions<T> {
  /** The raw term, straight off the control. Trimming is this hook's job, not the caller's. */
  query: string;
  /**
   * The cache key for a settled term. A function and not a prefix string, so a caller can compose
   * whatever its read is keyed by — and, more to the point, so two callers can compose the SAME
   * key and share one answer. `user-card` and `location-search` both key `['location-search', t]`
   * deliberately: the ficha's address field and the header's address search are the same read.
   */
  queryKey: (term: string) => readonly unknown[];
  search: (term: string) => Promise<T>;
}

// Type a term, wait for it to settle, search once. Extracted from `location-search` (CBX-11387) so
// the «Usuario» ficha's address field could reuse it: duplicating it would let the two diverge at
// the first correction, and the tempting alternative — importing `useLocationSearch` across slices
// — would create an edge outside `scripts/layout-allowed-edges.txt` and fail the required
// `ci-layout` check. `care/src/lib` is reached as `../../../lib`, which the gate does not count as
// a cross-slice edge.
export function useDebouncedSearch<T>({
  query,
  queryKey,
  search,
}: DebouncedSearchOptions<T>): UseDebouncedSearch<T> {
  // TRIMMED once, up front, and everything below reads this instead of the raw term. Three things
  // ride on it: the server trims before applying its own floor, so measuring the raw term would
  // let «   ab   » past and 400 on the far side; keying the raw one would open a second cache
  // entry for one search, which is how two callers sharing a key stop sharing an answer; and a
  // trailing space no longer restarts the debounce, since nothing about the search changed.
  const term = query.trim();
  // Seeded with the first term, so a control mounted pre-filled searches on sight instead of
  // idling through a debounce window the agent never typed in.
  const [debouncedTerm, setDebouncedTerm] = useState(term);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(term);
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [term]);

  // The hint answers the TYPED term and the request the debounced one, so the floor is asked twice.
  const isTooShort = !isSearchable(term);

  const { data, isError, refetch } = useQuery({
    queryKey: queryKey(debouncedTerm),
    queryFn: isSearchable(debouncedTerm) ? () => search(debouncedTerm) : skipToken,
  });

  return {
    data,
    // Derived from the settled query rather than mirrored into state: `data` only ever belongs to
    // the key it came from, so a caller can never attribute a result to a term still debouncing.
    resolvedQuery: data === undefined ? undefined : debouncedTerm,
    isTooShort,
    // Not TanStack's `isPending`, which a skipped query reports forever: a caller branching on it
    // would skeleton for as long as the term stays under the floor.
    isSearching: !isTooShort && !isError && data === undefined,
    isError,
    refetch: () => void refetch(),
  };
}
